<template>
  <div class="sigdef-response-form">
    <b-card>
      <b-card-title>{{ formTitle }}</b-card-title>

      <b-alert v-if="isClarificationResponse" variant="warning" show>
        <div class="clarification-heading">
          MSDH has asked for clarification on your previous response.
        </div>
        <!-- The reviewer's own words. Kept visually separate from the surrounding
             alert copy: this is the thing being answered, not instructions. -->
        <blockquote v-if="clarificationNotes" class="clarification-request">
          <div class="clarification-request-label">What MSDH is asking for</div>
          <p class="clarification-request-text mb-0">{{ clarificationNotes }}</p>
        </blockquote>
        <p class="clarification-instructions mb-0">
          Answer below. Your answer is added to the response history as a new
          entry &mdash; the response you sent before stays on file exactly as you
          submitted it.
        </p>
      </b-alert>

      <b-alert v-else-if="isFollowUp" variant="info" show>
        This deficiency already has
        {{ priorResponseCount === 1 ? 'a response' : `${priorResponseCount} responses` }}
        on file<span v-if="latestStatusLabel"> (most recent: {{ latestStatusLabel }})</span>.
        Your submission is added to the response history &mdash; it does not replace
        what you sent before. Use this to file proof of correction, progress
        documentation, or an extension request.
      </b-alert>

      <b-alert v-if="errorMessage" variant="danger" show dismissible @dismissed="errorMessage = null">
        {{ errorMessage }}
      </b-alert>

      <b-alert v-if="successMessage" variant="success" dismissible @dismissed="successMessage = null">
        {{ successMessage }}
      </b-alert>

      <b-form @submit.prevent="submitResponse">
        <!-- Deficiency Information (Read-only) -->
        <b-card bg-variant="light" class="mb-3" v-if="deficiency">
          <h6>Deficiency Information</h6>
          <p class="mb-1">
            <strong>ID:</strong> {{ deficiency.sigdef_id }}
          </p>
          <p class="mb-1">
            <strong>PWS ID:</strong> {{ deficiency.pws_id }}
          </p>
          <p class="mb-1">
            <strong>Category:</strong>
            {{ deficiency.sanitary_srvey_cat }} - {{ deficiency.sanitary_srvey_cat_desc }}
          </p>
          <p class="mb-1">
            <strong>Deficiency Type:</strong> {{ deficiency.description_cv }}
          </p>
          <p class="mb-1" v-if="deficiency.facility_name">
            <strong>Facility:</strong> {{ deficiency.facility_name }}
            <span v-if="deficiency.facility_status">
              ({{ deficiency.facility_status }})
            </span>
          </p>
          <p class="mb-1" v-if="deficiency.description_txt">
            <strong>Description:</strong> {{ deficiency.description_txt }}
          </p>
          <p class="mb-1" v-if="deficiency.comments">
            <strong>Comments:</strong> {{ deficiency.comments }}
          </p>
          <p class="mb-0" v-if="deficiency.determination_date">
            <strong>Determination Date:</strong> {{ formatDate(deficiency.determination_date) }}
          </p>
        </b-card>

        <!-- Response Text -->
        <b-form-group
          label="Response Details *"
          label-for="response-text"
          :description="isFollowUp
            ? 'Describe the corrective actions completed since your last response, or the plan and proposed completion date if you are requesting an extension'
            : 'Describe the corrective actions taken or planned to resolve this deficiency'"
        >
          <b-form-textarea
            id="response-text"
            v-model="form.response_text"
            rows="8"
            required
            placeholder="Enter detailed response describing corrective actions..."
          ></b-form-textarea>
        </b-form-group>

        <!-- Resolution Date -->
        <b-form-group
          label="Resolution Date"
          label-for="resolution-date"
          description="Date when corrective actions were completed"
        >
          <b-form-input
            id="resolution-date"
            v-model="form.resolution_date"
            type="date"
            :max="todayDate"
          ></b-form-input>
        </b-form-group>

        <!-- Existing Files (in edit mode) -->
        <div v-if="isEditMode && existingFiles.length > 0" class="mt-3">
          <h6>Attached Files:</h6>
          <b-list-group>
            <b-list-group-item
              v-for="file in existingFiles"
              :key="file.id"
              class="d-flex justify-content-between align-items-center"
            >
              <span>
                <b-icon :icon="getFileIcon(file)"></b-icon>
                {{ file.filename }}
                <small class="text-muted">({{ formatFileSize(file.file_size) }})</small>
              </span>
              <div>
                <b-button
                  v-if="canPreview(file)"
                  variant="outline-primary"
                  size="sm"
                  @click="previewFile(file)"
                  class="mr-2"
                >
                  <b-icon icon="eye"></b-icon> Preview
                </b-button>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="downloadFile(file.id)"
                  class="mr-2"
                >
                  <b-icon icon="download"></b-icon> Download
                </b-button>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="deleteFile(file)"
                >
                  <b-icon icon="trash"></b-icon> Delete
                </b-button>
              </div>
            </b-list-group-item>
          </b-list-group>
          <small class="text-muted d-block mt-2">Note: File attachments can be modified when updating a response.</small>
        </div>

        <!-- File Upload -->
        <b-form-group
          label="Supporting Documentation"
          :description="isEditMode ? 'Upload additional photos, reports, or other documentation (PDF, JPG, PNG, DOCX)' : 'Upload photos, reports, or other documentation (PDF, JPG, PNG, DOCX)'"
        >
          <FileUpload
            ref="fileUpload"
            category="sigdef_response"
            :auto-upload="true"
            :max-files="5"
            :max-file-size="10"
            @upload-complete="onUploadComplete"
            @upload-error="onUploadError"
          />
        </b-form-group>

        <!-- Submit Button -->
        <b-alert v-if="errorMessage" variant="danger" show dismissible @dismissed="errorMessage = null" class="mb-3">
          {{ errorMessage }}
        </b-alert>
        <div class="text-right">
          <b-button type="submit" variant="primary" :disabled="submitting">
            <b-spinner small v-if="submitting"></b-spinner>
            {{ submitButtonText }}
          </b-button>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios';
import FileUpload from './FileUpload.vue';

export default {
  name: 'SigDefResponseForm',
  components: {
    FileUpload,
  },
  props: {
    deficiency: {
      type: Object,
      required: true,
    },
    existingResponse: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: {
        response_text: '',
        resolution_date: '',
      },
      uploadedFileIds: [],
      existingFiles: [],
      submitting: false,
      errorMessage: null,
      successMessage: null,
    };
  },
  watch: {
    existingResponse: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.form.response_text = newVal.response_text || '';
          this.form.resolution_date = newVal.resolution_date || '';
          this.existingFiles = newVal.files || [];
        } else {
          // Switched from editing a response to composing a new one --
          // start from a blank form rather than the previous response text
          this.form.response_text = '';
          this.form.resolution_date = '';
          this.existingFiles = [];
        }
      },
    },
    // Selecting a different deficiency without unmounting the form
    deficiency() {
      if (!this.existingResponse) {
        this.form.response_text = '';
        this.form.resolution_date = '';
        this.existingFiles = [];
      }
      this.uploadedFileIds = [];
      this.errorMessage = null;
      this.successMessage = null;
      if (this.$refs.fileUpload) {
        this.$refs.fileUpload.reset();
      }
    },
  },
  computed: {
    isEditMode() {
      return this.existingResponse !== null;
    },
    // Number of responses already on file for this deficiency. The list view
    // supplies response_count; the detail view supplies the full responses array.
    priorResponseCount() {
      if (!this.deficiency) return 0;
      if (Array.isArray(this.deficiency.responses)) {
        return this.deficiency.responses.length;
      }
      if (this.deficiency.response_count) return this.deficiency.response_count;
      return this.deficiency.response_id ? 1 : 0;
    },
    // A new (non-edit) submission on a deficiency that already has responses
    isFollowUp() {
      return !this.isEditMode && this.priorResponseCount > 0;
    },
    // Status of the most recent response. The list view supplies it flat as
    // response_status; the detail view supplies the full responses array.
    latestStatus() {
      if (!this.deficiency) return null;
      if (this.deficiency.response_status) return this.deficiency.response_status;
      if (Array.isArray(this.deficiency.responses) && this.deficiency.responses.length > 0) {
        return this.deficiency.responses[0].status;
      }
      return null;
    },
    latestStatusLabel() {
      const statusMap = {
        pending: 'Pending',
        under_review: 'Under Review',
        accepted: 'Accepted',
        active_schedule: 'Active Schedule',
        rejected: 'Rejected',
        needs_clarification: 'Needs Clarification',
      };
      const status = this.latestStatus;
      if (!status) return '';
      return statusMap[status] || status;
    },
    // Answering a clarification request is a follow-up, not an edit -- but it
    // needs the reviewer's question in front of the operator, or they are typing
    // into a blank box with no idea what was asked.
    isClarificationResponse() {
      return this.isFollowUp && this.latestStatus === 'needs_clarification';
    },
    clarificationNotes() {
      if (!this.deficiency) return '';
      if (this.deficiency.reviewer_notes) return this.deficiency.reviewer_notes;
      if (Array.isArray(this.deficiency.responses) && this.deficiency.responses.length > 0) {
        return this.deficiency.responses[0].reviewer_notes || '';
      }
      return '';
    },
    formTitle() {
      if (this.isEditMode) return 'Update Response to Significant Deficiency';
      if (this.isClarificationResponse) return 'Respond to Clarification Request';
      if (this.isFollowUp) return 'Submit Additional Documentation';
      return 'Submit Response to Significant Deficiency';
    },
    submitButtonText() {
      if (this.submitting) {
        return this.isEditMode ? 'Updating...' : 'Submitting...';
      }
      if (this.isEditMode) return 'Update Response';
      if (this.isClarificationResponse) return 'Submit Clarification';
      return this.isFollowUp ? 'Submit Documentation' : 'Submit Response';
    },
    todayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
  },
  methods: {
    // Keep formatDate for backward compatibility with tests
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    // Keep formatFileSize for backward compatibility with tests
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / (k ** i)).toFixed(1))} ${sizes[i]}`;
    },
    // Keep getFileIcon for backward compatibility with tests
    getFileIcon(file) {
      const filename = file.filename || file.name;
      const ext = filename.split('.').pop().toLowerCase();
      switch (ext) {
        case 'pdf':
          return 'file-earmark-pdf';
        case 'jpg':
        case 'jpeg':
        case 'png':
          return 'file-earmark-image';
        case 'docx':
        case 'doc':
          return 'file-earmark-word';
        default:
          return 'file-earmark';
      }
    },
    // Keep canPreview for backward compatibility with tests
    canPreview(file) {
      const mimeType = file.mime_type || '';
      return mimeType === 'application/pdf' || mimeType.startsWith('image/');
    },
    downloadFile(fileId) {
      this.$emit('download-file', fileId);
    },
    previewFile(file) {
      this.$emit('preview-file', file);
    },
    deleteFile(file) {
      this.$emit('delete-file', file);
    },
    onUploadComplete(files) {
      // Must ACCUMULATE. FileUpload auto-uploads on every drop and emits only
      // that batch, so assigning here discarded every batch but the last -- the
      // operator saw all their files listed as uploaded while only the final
      // batch was ever attached to the response. See
      // server/docs/SIGDEF_FOLLOWUP_NOTES.md for the orphaned-file backlog.
      const newIds = files.map((f) => f.id);
      this.uploadedFileIds = [
        ...this.uploadedFileIds,
        ...newIds.filter((id) => !this.uploadedFileIds.includes(id)),
      ];
    },
    onUploadError(error) {
      // eslint-disable-next-line no-console
      console.error('File upload error:', error);
      this.errorMessage = 'Failed to upload one or more files';
    },
    async submitResponse() {
      this.submitting = true;
      this.errorMessage = null;
      this.successMessage = null;

      try {
        const responseData = {
          response_text: this.form.response_text,
          resolution_date: this.form.resolution_date || null,
        };

        let responseId;

        if (this.isEditMode) {
          // Update existing response
          await axios.put(
            `/api/sigdefs/response/${this.existingResponse.id}`,
            responseData,
          );
          responseId = this.existingResponse.id;

          // Attach any new files uploaded during edit
          if (this.uploadedFileIds.length > 0) {
            await axios.post(
              `/api/sigdefs/response/${responseId}/attach-files`,
              { file_ids: this.uploadedFileIds },
            );
          }

          this.successMessage = 'Response updated successfully!';
        } else {
          // Create new response
          const responseResult = await axios.post(
            `/api/sigdefs/${this.deficiency.sigdef_id}/response`,
            responseData,
          );
          responseId = responseResult.data.response_id;

          // Attach files to response (files were auto-uploaded when selected)
          if (this.uploadedFileIds.length > 0) {
            await axios.post(
              `/api/sigdefs/response/${responseId}/attach-files`,
              { file_ids: this.uploadedFileIds },
            );
          }

          this.successMessage = 'Response submitted successfully!';
        }

        // Emit success event
        this.$emit('response-submitted', {
          response_id: responseId,
          sigdef_id: this.deficiency.sigdef_id,
        });

        // Reset form
        this.form = {
          response_text: '',
          resolution_date: '',
        };
        this.uploadedFileIds = [];
        if (this.$refs.fileUpload) {
          this.$refs.fileUpload.reset();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error submitting response:', error);
        // eslint-disable-next-line no-console
        console.log('error.response:', error.response);
        // eslint-disable-next-line no-console
        console.log('error.response.data:', error.response && error.response.data);
        const errorMsg = (error.response && error.response.data && error.response.data.error)
          || `Failed to ${this.isEditMode ? 'update' : 'submit'} response. Please try again.`;
        // eslint-disable-next-line no-console
        console.log('Setting errorMessage to:', errorMsg);
        this.errorMessage = errorMsg;
        // eslint-disable-next-line no-console
        console.log('this.errorMessage is now:', this.errorMessage);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.sigdef-response-form {
  max-width: 900px;
  margin: 0 auto;
}

.clarification-heading {
  font-weight: 600;
  margin-bottom: 0.75rem;
}

/* Lift the reviewer's request off the amber alert background so it reads as a
   quoted message rather than as more of the surrounding boilerplate. */
.clarification-request {
  background-color: #fff;
  border-left: 4px solid #d39e00;
  border-radius: 0.25rem;
  padding: 0.75rem 1rem;
  margin: 0 0 0.75rem;
}

.clarification-request-label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #856404;
  margin-bottom: 0.35rem;
}

.clarification-request-text {
  /* Reviewer notes are free-form and frequently numbered or multi-line --
     keep the line breaks they typed. */
  white-space: pre-wrap;
  font-size: 1.05rem;
  line-height: 1.5;
  color: #212529;
}

.clarification-instructions {
  font-size: 0.875rem;
  opacity: 0.85;
}
</style>
